/**
 * /admin/leads/import — CSV bulk-intake wizard.
 *
 * Flow:
 *   1. Operator drops or picks a .csv. Tap "Preview" — backend parses
 *      + validates per row WITHOUT writing.
 *   2. The preview table shows every row with inline error chips, plus
 *      a summary card (total / valid / invalid / dup counts).
 *   3. "Commit valid rows" sends the parsed rows back to the commit
 *      endpoint; the backend re-validates server-side and inserts in
 *      per-row savepoints.
 *
 * A "Download template" link sits in the header so a newcomer never
 * has to guess columns.
 */
import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Upload, Download, FileText, CheckCircle2, AlertTriangle,
  Loader2, PlayCircle, X,
} from "lucide-react";

import {
  csvTemplateUrl, previewLeadsCsv, commitLeadsCsv,
} from "../../../api/leadEngine.api";

export default function CsvImport() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {rows, summary, header_seen}
  const [error, setError] = useState(null);
  const [commitResult, setCommitResult] = useState(null);

  function reset() {
    setFile(null);
    setPreview(null);
    setError(null);
    setCommitResult(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function handlePreview() {
    if (!file) return;
    setBusy(true);
    setError(null);
    setPreview(null);
    setCommitResult(null);
    try {
      const { data } = await previewLeadsCsv(file);
      setPreview(data);
    } catch (e) {
      setError(
        e.response?.data?.detail
        || (typeof e.response?.data === "string" ? e.response.data : null)
        || e.message
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleCommit() {
    if (!preview) return;
    const validRows = preview.rows.filter((r) => r.is_valid).map((r) => r.data);
    if (validRows.length === 0) {
      setError("No valid rows to commit. Fix the CSV and re-preview.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data } = await commitLeadsCsv(validRows);
      setCommitResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) { setFile(f); setPreview(null); setCommitResult(null); }
  }

  return (
    <div className="p-6 max-w-6xl">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <header className="mb-5 flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Import leads from CSV</h1>
          <p className="text-sm text-text-secondary mt-1">
            Upload → preview validation errors → commit valid rows. Single-lead
            add: <Link to="/admin/leads/add" className="text-button-primary hover:underline">/admin/leads/add</Link>.
          </p>
        </div>
        <a
          href={csvTemplateUrl()}
          className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5"
        >
          <Download className="w-4 h-4" /> Download template
        </a>
      </header>

      {commitResult ? (
        <ResultCard result={commitResult} onAgain={reset} />
      ) : (
        <>
          {/* Upload card */}
          <div className="bg-bg-primary border border-border-default rounded-2xl p-4 mb-4">
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-emerald-500/30 hover:border-emerald-500/60 rounded-md p-5 cursor-pointer transition-colors"
            >
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  setPreview(null);
                  setCommitResult(null);
                }}
              />
              {file ? (
                <div className="flex items-center gap-3 text-sm">
                  <FileText className="w-5 h-5 text-emerald-300 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-mono text-text-primary truncate">{file.name}</div>
                    <div className="text-text-secondary text-xs">{(file.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); reset(); }}
                    className="ml-auto text-xs text-text-secondary hover:text-text-primary"
                  >
                    Replace
                  </button>
                </div>
              ) : (
                <div className="text-center text-sm text-emerald-300">
                  <Upload className="w-5 h-5 inline-block mr-2 -mt-0.5" />
                  Drop a <code className="px-1 bg-bg-secondary rounded">.csv</code> here, or click to pick.
                  <div className="text-text-secondary text-xs mt-1">
                    Required columns: <code>name, email, segment, context</code>. Optional:{" "}
                    <code>phone, org, role</code>.
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                onClick={handlePreview}
                disabled={!file || busy}
                className="px-3 py-1.5 text-sm rounded-md bg-button-primary text-white inline-flex items-center gap-1.5 disabled:opacity-50 hover:brightness-110"
              >
                {busy
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Parsing…</>
                  : <><PlayCircle className="w-3.5 h-3.5" /> Preview</>}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 px-3 py-2 text-sm flex items-start gap-2 mb-4">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="min-w-0 whitespace-pre-wrap">{String(error)}</div>
            </div>
          )}

          {/* Preview */}
          {preview && (
            <>
              <SummaryCard summary={preview.summary} />
              <PreviewTable rows={preview.rows} />
              <div className="flex items-center justify-end gap-2 mt-3">
                <button
                  onClick={reset}
                  className="px-3 py-1.5 text-sm rounded-md border border-border-default hover:bg-bg-secondary"
                >
                  Start over
                </button>
                <button
                  onClick={handleCommit}
                  disabled={busy || preview.summary.valid === 0}
                  className="px-3 py-1.5 text-sm rounded-md bg-emerald-600 text-white inline-flex items-center gap-1.5 disabled:opacity-50 hover:bg-emerald-500"
                >
                  {busy
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Committing…</>
                    : <><CheckCircle2 className="w-3.5 h-3.5" /> Commit {preview.summary.valid} valid row{preview.summary.valid === 1 ? "" : "s"}</>}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ summary }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-4">
      <Stat label="Total" value={summary.total} />
      <Stat label="Valid" value={summary.valid} tone="emerald" />
      <Stat label="Invalid" value={summary.invalid} tone="rose" />
      <Stat label="Dup in batch" value={summary.duplicates_batch} tone="amber" />
      <Stat label="Dup vs leads" value={summary.duplicates_lead} tone="amber" />
      <Stat label="Dup vs users" value={summary.duplicates_user} tone="amber" />
      <Stat label="Missing required" value={summary.missing_required} tone="rose" />
    </div>
  );
}

function Stat({ label, value, tone }) {
  const TONE = {
    emerald: "text-emerald-300",
    rose:    "text-rose-300",
    amber:   "text-amber-300",
  };
  return (
    <div className="rounded-md bg-bg-secondary/40 border border-border-default p-2">
      <div className={`font-mono text-lg ${TONE[tone] || "text-text-primary"}`}>{value}</div>
      <div className="text-text-secondary text-[10px] uppercase tracking-wider">{label}</div>
    </div>
  );
}

function PreviewTable({ rows }) {
  return (
    <div className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-table-header text-left text-[11px] uppercase tracking-widest text-text-secondary">
          <tr>
            <th className="px-3 py-2 w-12 text-right">#</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Email</th>
            <th className="px-3 py-2">Segment</th>
            <th className="px-3 py-2">Org</th>
            <th className="px-3 py-2">Errors</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.row_no} className="border-t border-border-default align-top">
              <td className="px-3 py-2 text-right font-mono text-text-secondary text-xs">{r.row_no}</td>
              <td className="px-3 py-2">
                {r.is_valid ? (
                  <span className="inline-flex items-center gap-1 text-emerald-300 text-xs">
                    <CheckCircle2 className="w-3 h-3" /> valid
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-rose-300 text-xs">
                    <X className="w-3 h-3" /> skip
                  </span>
                )}
              </td>
              <td className="px-3 py-2">{r.data.name || <span className="text-text-secondary">—</span>}</td>
              <td className="px-3 py-2 font-mono text-xs">{r.data.email || "—"}</td>
              <td className="px-3 py-2 text-text-secondary text-xs">{r.data.segment || "—"}</td>
              <td className="px-3 py-2 text-text-secondary text-xs">{r.data.org || "—"}</td>
              <td className="px-3 py-2 space-y-0.5">
                {r.errors.length === 0
                  ? <span className="text-text-secondary text-xs">—</span>
                  : r.errors.map((e, i) => (
                      <div key={i} className="text-rose-200 text-[11px]">· {e}</div>
                    ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultCard({ result, onAgain }) {
  return (
    <div className="bg-bg-primary border border-emerald-500/40 rounded-2xl p-5 space-y-3">
      <div className="flex items-center gap-2 text-emerald-200 font-semibold">
        <CheckCircle2 className="w-5 h-5" />
        Done — {result.created} lead{result.created === 1 ? "" : "s"} added · {result.skipped} skipped
      </div>
      {result.skip_reasons?.length > 0 && (
        <details className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-200 text-sm">
          <summary className="cursor-pointer font-medium">
            {result.skip_reasons.length} row{result.skip_reasons.length === 1 ? "" : "s"} skipped — see why
          </summary>
          <ul className="mt-2 ml-3 space-y-1 text-xs">
            {result.skip_reasons.slice(0, 50).map((s, i) => (
              <li key={i}>
                row {s.row_no} ({s.email || "no email"}): {s.errors.join("; ")}
              </li>
            ))}
          </ul>
        </details>
      )}
      <div className="flex items-center gap-2 pt-1">
        <Link
          to="/admin/leads"
          className="px-3 py-1.5 text-sm rounded-md bg-button-primary text-white inline-flex items-center gap-1.5 hover:brightness-110"
        >
          Go to pipeline →
        </Link>
        <button
          onClick={onAgain}
          className="px-3 py-1.5 text-sm rounded-md border border-border-default hover:bg-bg-secondary"
        >
          Upload another
        </button>
      </div>
    </div>
  );
}
